import api from "../utils/api";
import { LOGOUT } from "./types";

export const directories = ({ ownerType, ownerUuid, contextType, contextUuid }) => async () => {
    const response = await api.get(
        `${buildScopePath({
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        })}/directories`
    );

    return response.data || response;
};

export const unzip = (uuid) => async (dispatch) => {
    try {
        const res = await api.post("/files/unzip", { uuid });
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const destroy = (uuid) => async (dispatch) => {
    try {
        const res = await api.delete(`/files/i/${uuid}`);
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const base64 = (uuid) => async (dispatch) => {
    try {
        const res = await api.get(`/files/i/${uuid}/s/base64`);
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}



function buildScopePath({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid
}) {
    let path = `/files/o/${ownerType}/${ownerUuid}`;

    if (contextType && contextUuid) {
        path += `/c/${contextType}/${contextUuid}`;
    }

    return path;
}

export const list = ({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid,
    directoryUuid
}) => async () => {
    const params = new URLSearchParams();

    if (directoryUuid) {
        params.append("directoryUuid", directoryUuid);
    }

    const query = params.toString();
    const url = `${buildScopePath({
        ownerType,
        ownerUuid,
        contextType,
        contextUuid
    })}${query ? `?${query}` : ""}`;

    const response = await api.get(url);

    return response.data || response;
};

export const createDirectory = ({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid,
    parentUuid,
    name
}) => async () => {
    const response = await api.post(
        `${buildScopePath({
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        })}/directory`,
        {
            parentUuid,
            name
        }
    );

    return response.data || response;
};

export const upload = ({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid,
    directoryUuid,
    file
}) => async () => {
    const form = new FormData();

    form.append("file", file);

    if (directoryUuid) {
        form.append("directoryUuid", directoryUuid);
    }

    const response = await api.post(
        `${buildScopePath({
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        })}/upload`,
        form,
        {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        }
    );

    return response.data || response;
};

export const download = ({ uuid, name }) => async () => {
    const response = await api.get(
        `/files/i/${uuid}/download`,
        {
            responseType: "blob"
        }
    );

    const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream"
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", name || "download");
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
        success: true
    };
};

export const open = ({ uuid, name }) => async () => {
    const popup = window.open("", "_blank");
    if (popup) popup.opener = null;
    const response = await api.get(
        `/files/i/${uuid}/download`,
        {
            responseType: "blob"
        }
    );

    const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream"
    });
    const url = window.URL.createObjectURL(blob);

    if (popup) {
        popup.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } else {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name || "download");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return {
        success: true
    };
};

export const downloadMany = ({
    files = [],
    directories = [],
    name = "files.zip"
}) => async () => {
    const response = await api.post(
        "/files/download",
        {
            files,
            directories
        },
        {
            responseType: "blob"
        }
    );

    const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/zip"
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
        success: true
    };
};

export const downloadContents = ({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid,
    directoryUuid,
    name = "folder-contents.zip"
}) => async () => {
    const params = new URLSearchParams();

    if (directoryUuid) {
        params.append("directoryUuid", directoryUuid);
    }

    const query = params.toString();
    const response = await api.get(
        `${buildScopePath({
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        })}/download${query ? `?${query}` : ""}`,
        {
            responseType: "blob"
        }
    );

    const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/zip"
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
        success: true
    };
};

export const renameFile = ({ uuid, name }) => async () => {
    const response = await api.put(
        `/files/i/${uuid}`,
        {
            name
        }
    );

    return response.data || response;
};

export const moveFile = ({ uuid, directoryUuid }) => async () => {
    const response = await api.put(
        `/files/i/${uuid}/move`,
        {
            directoryUuid
        }
    );

    return response.data || response;
};

export const deleteFile = ({ uuid }) => async () => {
    const response = await api.delete(`/files/i/${uuid}`);

    return response.data || response;
};

export const renameDirectory = ({ uuid, name }) => async () => {
    const response = await api.put(
        `/files/directories/i/${uuid}`,
        {
            name
        }
    );

    return response.data || response;
};

export const moveDirectory = ({ uuid, directoryUuid }) => async () => {
    const response = await api.put(
        `/files/directories/i/${uuid}/move`,
        {
            directoryUuid
        }
    );

    return response.data || response;
};

export const deleteDirectory = ({ uuid }) => async () => {
    const response = await api.delete(`/files/directories/i/${uuid}`);

    return response.data || response;
};

export const listShares = ({ fileUuid, directoryUuid }) => async () => {
    const params = new URLSearchParams();

    if (fileUuid) {
        params.append("fileUuid", fileUuid);
    }

    if (directoryUuid) {
        params.append("directoryUuid", directoryUuid);
    }

    const response = await api.get(`/files/shares?${params.toString()}`);

    return response.data || response;
};

export const applyShares = ({
    files = [],
    directories = [],
    shares = []
}) => async () => {
    const response = await api.post(
        "/files/shares",
        {
            files,
            directories,
            shares
        }
    );

    return response.data || response;
};

export const eligibleShares = ({
    ownerType,
    ownerUuid,
    contextType,
    contextUuid,
    q
}) => async () => {
    let url = `/files/share/eligible/o/${ownerType}/${ownerUuid}`;
    if (contextType && contextUuid) url += `/c/${contextType}/${contextUuid}`;
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    const query = params.toString();
    if (query)  url += `?${query}`;
    const response = await api.get(url);
    return response.data || response;
};
